/* @flow */
import { getterFromKeyPath } from 'trustar/utils';
import { createSelector } from 'reselect';
import { Map, List, OrderedMap } from 'immutable';
import isString from 'lodash.isstring';
import { filter, pipe } from 'ramda';
import { alphabetize } from 'trustar/utils';
import createDebugger from 'debug';
import { IO } from 'trustar/fp';
import { sortBy } from 'trustar/utils/utils.js';

const debug = createDebugger('app:modules:users:getters'); // eslint-disable-line no-unused-vars

import { User as UserRecord } from './types.js';
import { companies } from 'modules/companies/getters';
import { hasPermission, company, profile } from 'modules/profile/getters';
import { determineAccessType } from 'modules/enclave/utils';

import { Role } from 'modules/roles/types.js';
import { roles } from 'modules/roles/getters';

export const usersMap = getterFromKeyPath(['users', 'users']);

export const autosuggestText = getterFromKeyPath(['users', 'autosuggestText']);

export const loading = getterFromKeyPath(['users', 'loading']);

export const displayOptions = getterFromKeyPath(['users', 'options']);

export const userListPopulated = createSelector(
  usersMap,
  (users: OrderedMap<any, any>) => {
    return users && users.size > 0;
  },
);

export const user = (guid: string) => {
  return createSelector(
    usersMap,
    (users: OrderedMap<any, any>) => users.get(guid),
  );
};

export const companyAdmins = createSelector(
  usersMap,
  pipe(
    filter(x => x.get('isCompanyAdmin', false)),
    x => x.toList()
  ),
);


/**
 * Format users into plain objects for use with the draft-js mentions plugin
 */
const formatMentionSuggestions = x => ({
  guid: x.get('guid'),
  name: x.get('firstName') + ' ' + x.get('lastName'), // Need this to be `name` for suggestions to work
  email: x.get('email'),
});

const filterByQuery = _str => user => {
  if (!_str) return true;
  const str = _str.toLowerCase();
  const name = user.name.toLowerCase();
  const email = user.email.toLowerCase();
  return name.includes(str) || email.includes(str);
};

/**
 * Get a an Array<Object> of user suggestions to be used when autocompleting the
 * users typing as they tag other users in their company
 */
export const suggestedUsers = createSelector(
  [usersMap, company, profile, autosuggestText],
  (...args) => {
    return IO.of(args) // Wrap for error handling
      .map(([ users, company, profile, query ]) => {
        return users
          .toSeq()
          .valueSeq()
          .filter(x => x.getIn(['company', 'guid']) === profile.getIn(['company', 'guid'])) // In my company...
          .filter(x => x.get('guid') !== profile.get('guid')) // ...but not me
          .map(formatMentionSuggestions)
          .filter(filterByQuery(query))
          .sort(sortBy('name', 'ASC'))
          .take(5) // Limit to 5
          .toJS();
      })
      .foldOr(err => {
        debug('Could not create company users list:', err);
        return []; // Always return array
      });
  }
);

// TODO
// Work in progress. Does not yet filter companies b/c the user object does not
// have a company ID on it
export const users = createSelector(
  [ usersMap, displayOptions, roles ],
  (users: OrderedMap<any, any>, options: Map<any, any>, roles: List<any>) => {
    const companyGuid = options.get('companyGuid');
    const enclaveId = options.get('enclaveId');
    const roleId = options.get('roleId');
    return users
      .filter(user => {
        if (!companyGuid && !enclaveId && !roleId) return true;

        if (companyGuid && user.getIn(['company', 'guid']) !== companyGuid) {
          return false;
        }

        if (enclaveId && !user.get('enclaves', Map()).keySeq().contains(enclaveId)) {
          return false;
        }

        // Test all top-level string values
        return user
          .toSeq()
          .valueSeq()
          .filter(isString);
      })
      .toList();
  },
);

export const roleOptions = createSelector(
  hasPermission('grant_internal_roles'),
  roles,
  (canAssignAdminRoles: boolean, roles: List<Role>) =>
    (canAssignAdminRoles
    ? roles
    : roles.filter(role => !role.get('internal')))
    .toList()
    .sort(alphabetize)
    .map(role => Map({
      value: role.get('name'),
      label: role.get('displayName'),
      ...role.toJS()
    }))
);

// Generate company options for add/edit user form, for Admins to select companies
export const companyOptions = createSelector(
  hasPermission('manage_companies'),
  companies,
  (canManageUsers: boolean, companies: Map<any, any>) =>
    !canManageUsers
      ? null
      : companies
        .toList()
        .sort(alphabetize)
        .map(company => Map({
          value: company.get('id'),
          label: company.get('name'),
          ...company.update('enclaves', e => e.toList()).toJS(),
        }))
);

// Given an enclave, set the access type type based on read/create/update fields
export const setAccessType = (enclave: any): any => {
  return enclave.set('maximumAccess', determineAccessType(enclave));
};

// Determine initial form values for user add/edit form
export const getInitialFormValues = (user: ?Map<any, any>) => createSelector(
  hasPermission('manage_companies'),
  company,
  roles,
  (canManageCompanies: boolean, company: Map<any, any>, roles: List<any>) => {
    return !user ?
      UserRecord({
        enclaves: canManageCompanies ? List() : company
          .get('enclaves')
          .sort(alphabetize)
          .map(setAccessType)
          .toMap()
          .mapKeys((k,v) => v.get('id')),
        company: canManageCompanies ? null : company
          .set('value', company.get('id'))
          .set('label', company.get('name'))
      }) :
      user
        .update('enclaves', enclaves =>
          enclaves
            .sort(alphabetize)
            .map(setAccessType))
        .update('company', c => c
          .set('value', c.get('id'))
          .set('label', c.get('name')))
        .update('role', r => r
          .set('value', r.get('name'))
          .set('label', r.get('displayName')));
  }
);



// WEBPACK FOOTER //
// ./src/app/modules/users/getters.js